import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../lib/auth.js'

export interface LeaveItem {
  readonly id: string
  readonly type: 'ANNUAL' | 'SICK' | 'UNPAID' | 'OTHER'
  readonly startsAt: string
  readonly endsAt: string
  readonly note: string | null
  readonly status: 'PENDING' | 'APPROVED' | 'DECLINED' | 'WITHDRAWN'
  readonly decisionNote: string | null
  readonly reviewedAt: string | null
  readonly createdAt: string
  readonly updatedAt: string
  readonly requester: { readonly id: string; readonly name: string; readonly jobTitle: string | null }
  readonly reviewedBy: { readonly id: string; readonly name: string } | null
}

export interface LeaveReviewPayload {
  readonly decision: 'APPROVED' | 'DECLINED'
  readonly decisionNote?: string
  readonly extendTaskIds?: string[]
}

export function useLeaveList(params: { status?: string; userId?: string } = {}) {
  const { api } = useAuth()
  const query = new URLSearchParams()
  if (params.status !== undefined) query.set('status', params.status)
  if (params.userId !== undefined) query.set('userId', params.userId)
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`
  return useQuery({
    queryKey: ['leave', 'list', params],
    queryFn: () => api.get<{ items: LeaveItem[] }>(`/api/leave${suffix}`),
  })
}

export function useLeaveDetail(id: string | null) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['leave', 'detail', id],
    enabled: id !== null,
    queryFn: () => api.get<LeaveItem>(`/api/leave/${encodeURIComponent(id!)}`),
  })
}

export interface LeaveOverlapItem {
  readonly id: string
  readonly reference: string
  readonly title: string
  readonly effectiveDeadlineAt: string
  readonly collides: boolean
}

export function useOverlappingTasks(id: string | null) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['leave', 'overlapping-tasks', id],
    enabled: id !== null,
    queryFn: () => api.get<LeaveOverlapItem[]>(`/api/leave/${encodeURIComponent(id!)}/overlapping-tasks`),
  })
}

export function useCreateLeave() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { type: LeaveItem['type']; startDate: string; endDate: string; note?: string }) =>
      api.post<LeaveItem>('/api/leave', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] })
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
      await queryClient.invalidateQueries({ queryKey: ['workload'] })
    },
  })
}

export function useReviewLeave(id: string) {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LeaveReviewPayload) =>
      api.post<LeaveItem>(`/api/leave/${encodeURIComponent(id)}/review`, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] })
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
      await queryClient.invalidateQueries({ queryKey: ['workload'] })
    },
  })
}

export function useWithdrawLeave(id: string) {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<LeaveItem>(`/api/leave/${encodeURIComponent(id)}/withdraw`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] })
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
      await queryClient.invalidateQueries({ queryKey: ['workload'] })
    },
  })
}
